import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { DownOutlined, LeftOutlined, SearchOutlined } from "@ant-design/icons";
import { HeaderAsociacion } from "../componentes/headerAsociacion";
import { BandaAsociacion } from "../componentes/bandaAsociacion";
import { CustomFooter } from "../componentes/footer";
import { DecoracionesPilotos, Paginacion, TarjetaPiloto } from "../componentes/pilotosAsociacion";
import { FranjaFija } from "../componentes/franjaFija";

const POR_PAGINA = 12;

// Para buscar sin importar mayúsculas ni tildes
const normalizar = (texto) =>
  (texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Página con todos los pilotos registrados en la asociación (de todas las categorías)
export const Pilotos = () => {
  const { id_asociacion } = useParams();
  const [asociacion, setAsociacion] = useState(null);
  const [pilotos, setPilotos] = useState(null); // null mientras carga
  const [busqueda, setBusqueda] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    const cargar = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_DOMINIO}/public/piloto-asociacion/${id_asociacion}`
        );
        if (response.status === 200) {
          const { asociacion: datosAsociacion, pilotos: lista } = response.data;
          setAsociacion(datosAsociacion?.[0] || null);
          setPilotos(lista || []);
        }
      } catch (error) {
        console.error("Error al obtener los pilotos:", error);
        setPilotos([]);
      }
    };
    cargar();
  }, [id_asociacion]);

  // sin repetidos y ordenados por apellido
  const todos = useMemo(() => {
    const porId = new Map();
    (pilotos || []).forEach((p) => porId.set(p.id, p));
    return [...porId.values()].sort((a, b) =>
      `${a.apellido} ${a.nombre}`.localeCompare(`${b.apellido} ${b.nombre}`, "es")
    );
  }, [pilotos]);

  const localidades = useMemo(
    () => [...new Set(todos.map((p) => p.localidad).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")),
    [todos]
  );

  const filtrados = useMemo(() => {
    const texto = normalizar(busqueda.trim());
    return todos.filter((p) => {
      if (localidad && p.localidad !== localidad) return false;
      if (!texto) return true;
      return normalizar(`${p.nombre} ${p.apellido} ${p.localidad || ""}`).includes(texto);
    });
  }, [todos, busqueda, localidad]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda, localidad]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const visibles = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  return (
    <div style={{ "--bandera-asoc": "url('/img/asoc-bandera-fondo.png')" }}>
      <HeaderAsociacion />
      <BandaAsociacion idAsociacion={asociacion?.id} logo={asociacion?.imagen} nombre={asociacion?.nombre} />

      <FranjaFija izq="#0B2941">
      <section className="pilotos-asoc pilotos-asoc--todos">
        <DecoracionesPilotos />

        <div className="pilotos-asoc-interior">
          <div className="pilotos-asoc-cabecera">
            <span className="pilotos-asoc-barra" />
            <h2 className="pilotos-asoc-titulo">Todos los pilotos</h2>
            {asociacion?.siglas && (
              <Link to={`/${asociacion.siglas}`} className="pilotos-asoc-ver-todos">
                <LeftOutlined /> Volver a {asociacion.siglas}
              </Link>
            )}
            {asociacion?.nombre && (
              <p className="pilotos-asoc-subtitulo">
                Pilotos registrados en <span>{asociacion.nombre}</span>
              </p>
            )}
          </div>

          {pilotos === null ? (
            <p className="pilotos-asoc-vacio">Cargando pilotos...</p>
          ) : todos.length === 0 ? (
            <p className="pilotos-asoc-vacio">No hay pilotos disponibles.</p>
          ) : (
            <>
              <div className="pilotos-asoc-filtros">
                <label className="pilotos-asoc-buscar">
                  <SearchOutlined />
                  <input
                    type="search"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre o localidad"
                    aria-label="Buscar piloto"
                  />
                </label>
                {localidades.length > 1 && (
                  <label className="pilotos-asoc-localidad">
                    <select value={localidad} onChange={(e) => setLocalidad(e.target.value)} aria-label="Localidad">
                      <option value="">Todas las localidades</option>
                      {localidades.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                    <DownOutlined />
                  </label>
                )}
                <span className="pilotos-asoc-contador">
                  {filtrados.length} {filtrados.length === 1 ? "piloto" : "pilotos"}
                </span>
              </div>

              {filtrados.length > 0 ? (
                <>
                  <div className="pilotos-asoc-grilla">
                    {visibles.map((piloto) => (
                      <TarjetaPiloto
                        key={piloto.id}
                        piloto={piloto}
                        idAsociacion={asociacion?.id || id_asociacion}
                        mostrarNumero={false}
                      />
                    ))}
                  </div>
                  <Paginacion pagina={pagina} total={totalPaginas} onCambiar={setPagina} />
                </>
              ) : (
                <p className="pilotos-asoc-vacio">No se encontraron pilotos con ese filtro.</p>
              )}
            </>
          )}
        </div>
      </section>
      </FranjaFija>

      <CustomFooter mostrarInstagram={false} />
    </div>
  );
};
